import {Rotate, ScaleLine, ZoomSlider} from 'ol/control';
import GIS from "./app"
import Zoom from "ol/control/Zoom";
import GeolocationControl from "./controls/geolocation";
import Layers from "./controls/layers";
import Legend from "./controls/legend";
import MousePosition from "ol/control/MousePosition";
import Swipe from "./controls/swipe";
import Measurement from "./controls/measurement";
import Toggle from "./controls/toggle";
import Hint from "./controls/hint";
import CoordinatesPanel from "./controls/coordinatespanel";
import {Control as olControl} from "ol/control";
import {ControlConfiguration, SwipeControlExtension} from "./configuration/control"
import { Emitter } from './base/emitter';
import Sidebar from "./controls/sidebar";

type ControlMap = {
    [key: string]: olControl;
}

export type ZoomOptions = {
    zoomInTipLabel?: string | undefined,
    zoomOutTipLabel?: string | undefined
    zoomInLabel: HTMLElement
    zoomOutLabel: HTMLElement
}

export type RotateOptions = {
    tipLabel?: string | undefined,
    label: HTMLElement,
    autoHide?: boolean | undefined,
}

class Control {
    private readonly GIS;
    /**
     * Array of all controls
     */
    public entries:olControl[] = [];
    private controlMap:ControlMap = {};
    private emitter = new Emitter();

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    init(configuration:ControlConfiguration[]) {
        if (!this.controlMap['sidebar']) {
            (this.controlMap['sidebar'] = new Sidebar(this.emitter)) &&
            this.entries.push(this.controlMap['sidebar']);
        }

        configuration.map(control => {
            switch (control.type) {
                case "openlayers":
                    switch (control.name) {
                        case "scaleline":
                            let scaleOptions = {};
                            if (control.options !== undefined) {
                                scaleOptions = {
                                    className: (control.options.className !== undefined) ? control.options.className : 'ol-scale-line',
                                    steps: (control.options.steps !== undefined) ? control.options.steps : 4,
                                    minWidth: (control.options.minWidth !== undefined) ? control.options.minWidth : undefined,
                                    maxWidth: (control.options.maxWidth !== undefined) ? control.options.maxWidth : undefined,
                                    bar: (control.options.bar !== undefined) ? control.options.bar : false,
                                    units: (control.options.units !== undefined) ? control.options.units : 'metric',
                                    text: (control.options.text !== undefined) ? control.options.text : false
                                }
                            }
                            this.controlMap[control.name] = new ScaleLine(scaleOptions);
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "zoom":

                            const zoomInLabel = document.createElement('i');
                            zoomInLabel.className = 'fal fa-plus';

                            const zoomOutLabel = document.createElement('i');
                            zoomOutLabel.className = 'fal fa-minus';

                            let zoomOptions:ZoomOptions = { zoomInLabel, zoomOutLabel };

                            if (control.options) {
                                zoomOptions = {
                                    ...zoomOptions,
                                    zoomInTipLabel: control.options.zoomInTipLabel,
                                    zoomOutTipLabel: control.options.zoomOutTipLabel
                                }
                            }
                            this.controlMap[control.name] = new Zoom(zoomOptions);
                            this.entries.push(this.controlMap[control.name]);

                               {
                                const zoomEl = (this.controlMap[control.name] as unknown as { element: HTMLElement }).element;
                                const inBtn = zoomEl.querySelector<HTMLButtonElement>('.ol-zoom-in');
                                const outBtn = zoomEl.querySelector<HTMLButtonElement>('.ol-zoom-out');
                                if (inBtn) {
                                    this.GIS.Localisation.bindTitle(
                                        inBtn,
                                        control.options?.i18n?.zoomInTipLabel,
                                        control.options?.zoomInTipLabel ?? inBtn.title ?? 'Zoom in'
                                    );
                                }
                                if (outBtn) {
                                    this.GIS.Localisation.bindTitle(
                                        outBtn,
                                        control.options?.i18n?.zoomOutTipLabel,
                                        control.options?.zoomOutTipLabel ?? outBtn.title ?? 'Zoom out'
                                    );
                                }
                            }
                            break;
                        case "rotate":
                            const rotateLabel = document.createElement('i');
                            rotateLabel.className = 'fal fa-location-arrow-up';

                            let rotateOptions: RotateOptions = {
                                label: rotateLabel,
                                tipLabel: control.options?.tipLabel,
                                autoHide: true,
                            };

                            if (control.options) {
                                rotateOptions = {
                                    ...rotateOptions,
                                    tipLabel: control.options.tipLabel
                                };
                            }
                            this.controlMap[control.name] = new Rotate(rotateOptions);
                            this.entries.push(this.controlMap[control.name]);

                            {
                                const rotateEl = (this.controlMap[control.name] as unknown as { element: HTMLElement }).element;
                                const rotateBtn = rotateEl.querySelector<HTMLButtonElement>('button');
                                if (rotateBtn) {
                                    this.GIS.Localisation.bindTitle(
                                        rotateBtn,
                                        control.options?.i18n?.tipLabel,
                                        control.options?.tipLabel ?? rotateBtn.title ?? 'Reset rotation'
                                    );
                                }
                            }
                            break;
                        case "zoomslider":
                            this.controlMap[control.name] = new ZoomSlider();
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "mouseposition":
                            const projection = this.GIS.Projection.getRegistered(control.epsg);
                            this.controlMap[control.name] = new MousePosition({
                                coordinateFormat: (coordinates) => {
                                    let x;
                                    let y;
                                    if (coordinates) {
                                        x = coordinates[0].toFixed(2);
                                        y = coordinates[1].toFixed(2);
                                    }

                                    return 'EPSG: ' + control.epsg + '  ' + x + ', ' + y;
                                },
                                projection: projection
                            });
                            this.entries.push(this.controlMap[control.name]);
                            break;
                    }
                    break;
                case "geolocation":
                    control.options && this.GIS.Geolocation.init(control.options);
                    break;
                case "custom":
                    switch (control.name) {
                        case "gps":
                            (this.controlMap[control.name] = new GeolocationControl(this.GIS, control.options)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "layers":
                            (this.controlMap[control.name] = new Layers(this.GIS, this.emitter, control.options)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "legend":
                            (this.controlMap[control.name] = new Legend(this.GIS, this.emitter, control.options)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "hint":
                            control.options && (this.controlMap[control.name] = new Hint(control.options)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "swipe":
                            (this.controlMap[control.name] = new Swipe(this.GIS, control.options)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "measurement":
                            control.options && (this.controlMap[control.name] = new Measurement(this.GIS, control.options, this.emitter)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "toggle":
                            control.options && (this.controlMap[control.name] = new Toggle(control.options, this.GIS)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                        case "mouseposition":
                            control.options && (this.controlMap[control.name] = new CoordinatesPanel(control.options, this.GIS)) &&
                            this.entries.push(this.controlMap[control.name]);
                            break;
                    }
                    break;
            }
        });

        this.addControlsToMap();
    }

    private addControlsToMap(): void {
        this.entries.forEach(control => {
            this.GIS.Map.olObject!.addControl(control);
        });
    }

    /**
     * Get ol control, only custom controls are supported.
     * @private
     * @param name of a control
     * @returns return Ol Control
     */
    public getControl(name:string) {
        return this.controlMap[name] ?? null;
    };
    /**
     * Clear the Control object.
     * Useful, when you need to re-init the map component.
     */
    public clear(): void {
        // Remove all controls from the map
        this.entries.forEach(control => {
            control.setMap(null); // Detach control from the map
        });

        // Clear the control map
        Object.keys(this.controlMap).forEach(key => {
            delete this.controlMap[key];
        });

        // Clear the entries array
        this.entries.length = 0;

        // Clear all controls from the map's control collection
        if (this.GIS.Map.olObject) {
            this.GIS.Map.olObject.getControls().clear();
        }
    }
    /**
     * Swipe layer tool activate.
     * @param name {string} Layer name
     */
    public swipeLayer(name:string) {
        if (this.controlMap['swipe']) {
            const control = this.controlMap['swipe'] as (olControl & SwipeControlExtension);
            control.swipeLayer(name);
        }  else {
            throw new Error('Swipe control has not been initialized.');
        }
    }
    /**
     * Swipe layer tool deactivate.
     */
    public swipeDeactivate () {
        if (this.controlMap['swipe']) {
            const control = this.controlMap['swipe'] as (olControl & SwipeControlExtension);
            control.deactivate();
        }  else {
            throw new Error('Swipe control has not been initialized.');
        }
    }
}

export default Control;